import { BehaviorSubject, Subscription } from 'rxjs';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  FacetCodes,
  NecessarySearchFacets,
  RrsFacet,
  RrsFacetValue,
  RrsSizeFacetGroup,
  SEARCHABLE_FACET_LENGTH,
  SizeFacets,
} from '@app/custom/features/rrs-product-listing/model';
import { AutoUnsubscribe } from '@app/shared/decorators';
import { UntypedFormControl } from '@angular/forms';
import { ICON_TYPE_LIST } from '@app/spartacus/configurations/icon/icon.model';
import { Params } from '@angular/router';
import { RrsFacetService } from '@app/custom/features/rrs-product-listing/services/rrs-facet-service/rrs-facet.service';
import { RrsEventsDispatcherService } from '@app/custom/features/rrs-tms/rrs-adobe-experience/events/services/rrs-events.dispatcher';
import { Facet } from '@spartacus/core';
import { parseFacetsUrlParams } from '@app/custom/features/rrs-product-listing/utils/rrs-facet.utils';

import { RrsFacetNavigationService } from '@app/custom/features/rrs-product-listing/services/rrs-facet-navigation/rrs-facet-navigation.service';
import { cloneDeep, findIndex, mergeWith, remove, uniq } from 'lodash';

@AutoUnsubscribe()
@Component({
  selector: 'rrs-facet',
  templateUrl: './rrs-facet.component.html',
  styleUrls: ['./rrs-facet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RrsFacetComponent implements OnInit {
  @Input() currentSearchQuery: string = '';

  private subscriptions = new Subscription();
  protected _facet!: RrsFacet;

  isDesktop$ = this.breakpointService.isUp(BREAKPOINT.lg);
  isDesktop = false;
  iconTypeList = ICON_TYPE_LIST;
  searchInput!: UntypedFormControl;

  facetValues$: BehaviorSubject<RrsFacetValue[]> = new BehaviorSubject<
    RrsFacetValue[]
  >([]);

  @Input()
  set facet(value: RrsFacet) {
    this._facet = value;
    this.facetValues$.next(
      value.name?.includes('size') ? this.sortSizes(value) : value.values ?? []
    );
    this.initializeSearchable();
  }

  get facet(): RrsFacet {
    return this._facet;
  }

  get shouldDisplayFacet(): boolean {
    return (
      this.facet.multiSelect ||
      (this.facet.values ?? []).some(
        (value) => !(value as RrsFacetValue).selected
      )
    );
  }
  currentQuery: any = {};
  queryValue: any[] = [];
  constructor(
    protected facetService: RrsFacetService,
    protected cd: ChangeDetectorRef,
    private breakpointService: BreakpointService,
    protected eventsDispatcher: RrsEventsDispatcherService,
    protected rrsFacetNavigationService: RrsFacetNavigationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.facetService.mobileDialogSelectedFacets$.subscribe((query) => {
        this.currentQuery = query;
        this.cd.detectChanges();
      })
    );
    this.subscriptions.add(
      this.isDesktop$.subscribe((res) => {
        if (res) {
          this.rrsFacetNavigationService.toggleFacetsDialog(false);
        }
        this.isDesktop = res;
      })
    );
  }

  openLink(event: KeyboardEvent): void {
    (event.target as HTMLElement).click();
    event.preventDefault();
  }

  getLinkParams(value: RrsFacetValue): Params {
    return this.facetService.getLinkParams(value?.query?.query?.value ?? '');
  }

  isSizeFacet(): boolean {
    return SizeFacets.includes(this.facet.name as FacetCodes);
  }

  isRatingFacet(): boolean {
    return this.facet.name === FacetCodes.RATING;
  }

  isSpecialCaseFacet(): boolean {
    return this.isRatingFacet() || this.isSizeFacet();
  }

  isMultiSelectFacet(): boolean {
    return !!this.facet.multiSelect && !this.isSpecialCaseFacet();
  }

  isSearchableFacet(): boolean {
    return (
      !this.isSpecialCaseFacet() &&
      (Number(this.facet?.values?.length) > SEARCHABLE_FACET_LENGTH ||
        NecessarySearchFacets.includes(this.facet.name as FacetCodes))
    );
  }

  isScrollableFacet(): boolean {
    return !this.isSpecialCaseFacet();
  }

  private initializeSearchable(): void {
    if (this.isSearchableFacet()) {
      this.searchInput = new UntypedFormControl();
      const searchInputSub = this.searchInput.valueChanges.subscribe((value) =>
        this.facetValues$.next(
          (this.facet?.values ?? []).filter(({ name }) =>
            name?.toLowerCase().includes(value.toLowerCase())
          )
        )
      );
      this.subscriptions.add(searchInputSub);
    }
  }

  getTranslationKey(facetName: string): string {
    return this.facetService.getTranslationKey(facetName);
  }

  sortSizes(value: RrsFacet): (RrsFacetValue | RrsSizeFacetGroup)[] {
    const letterSizesOrder = ['S', 'SM', 'M', 'L', 'ML', 'XL', 'XXL'];
    const slashSizes = ['SM', 'ML'];

    const sortFn = (values: RrsFacetValue[]): RrsFacetValue[] =>
      [...values]
        .sort((a, b) => {
          const isANumeric = !isNaN(parseFloat(a.name || ''));
          const isBNumeric = !isNaN(parseFloat(b.name || ''));

          if (isANumeric && isBNumeric) {
            return parseFloat(a.name!) - parseFloat(b.name!);
          }
          if (isANumeric) return -1;
          if (isBNumeric) return 1;
          if (!letterSizesOrder.includes(a.name || '')) return 1;
          if (!letterSizesOrder.includes(b.name || '')) return -1;
          return (
            letterSizesOrder.indexOf(a.name!) -
            letterSizesOrder.indexOf(b.name!)
          );
        })
        .map((value) => {
          if (slashSizes.includes(value.name || '')) {
            let valueCopy = structuredClone(value);
            valueCopy.name =
              valueCopy.name?.slice(0, 1) + '/' + valueCopy.name?.slice(1);
            return valueCopy;
          }
          return value;
        });

    return (value.values ?? []).map((value) => {
      if ((value as RrsSizeFacetGroup).values) {
        return {
          ...value,
          values: sortFn((value as RrsSizeFacetGroup).values!),
        };
      } else if ((value as RrsSizeFacetGroup).subgroups) {
        return {
          ...value,
          subgroups: (value as RrsSizeFacetGroup).subgroups!.map((subgroup) => {
            return {
              ...subgroup,
              values:
                subgroup.name === FacetCodes.SIZE_BOY_LITTLE_KID ||
                subgroup.name === FacetCodes.SIZE_GIRL_LITTLE_KID
                  ? subgroup.values!
                  : sortFn(subgroup.values!),
            };
          }),
        };
      }

      return value;
    });
  }

  dispatchFacetChangeEvent(
    facetData: Facet,
    value: RrsFacetValue,
    isSelected: boolean
  ): void {
    if (value.name && !this.isDesktop) {
      const facet = cloneDeep(this.facet);
      if (facet && facet.values?.length) {
        facet.values[
          findIndex(facet.values, (val) => val.name === value.name)
        ] = {
          ...value,
          selected: true,
        };
        this.facet = facet;
      }

      const val = parseFacetsUrlParams(
        decodeURIComponent(this.getLinkParams(value)?.facetFilters)
      );
      if (
        (this._facet.multiSelect && isSelected && !this.isSizeFacet()) ||
        ((!this._facet.multiSelect || this.isSizeFacet()) &&
          !this.isSelected(value, facetData))
      ) {
        this.facetService.mobileDialogSelectedFacets$.next(
          mergeWith({}, this.currentQuery, val, (objValue, srcValue) => {
            if (Array.isArray(objValue)) {
              return uniq([...objValue, ...srcValue]);
            }
            return undefined;
          })
        );
      } else {
        const currentQuery = cloneDeep(this.currentQuery);
        const facetName = this.isSizeFacet()
          ? facetData.name
          : this._facet.name;
        if (facetName)
          remove(currentQuery[facetName], (res) => {
            return res == value.name;
          });
        this.facetService.mobileDialogSelectedFacets$.next(currentQuery);
      }
    }
    this.eventsDispatcher.dispatchFacetChangeEvent(
      `${this._facet.name} - ${value.name}`,
      isSelected ? 'remove' : 'add'
    );
    if (!isSelected) {
      this.eventsDispatcher.dispatchFacetClickEvent(
        `${this._facet.name}:${value}`
      );
    }
  }

  onCheckboxChange(facet: Facet, event: Event, value: RrsFacetValue): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    this.dispatchFacetChangeEvent(facet, value, isChecked);
  }
  isSelected(value: any, facet: any): boolean {
    if (
      this._facet.name &&
      this.currentQuery[this._facet.name] &&
      !this.isSizeFacet()
    ) {
      return this.currentQuery[this._facet.name].indexOf(value.name) >= 0;
    }
    if (this._facet.name && this.isSizeFacet() && this._facet?.values?.length) {
      let isSelected = false;
      for (let val of this._facet?.values) {
        if (
          val.name &&
          this.currentQuery[facet.name] &&
          !isSelected &&
          this.currentQuery[facet.name]?.indexOf(value.name) >= 0
        ) {
          isSelected = true;
          break;
        }
      }
      return isSelected;
    }
    return false;
  }
}
